/**
 * The windows plugins add: which exist (from the plugin list), and what each open one shows.
 *
 * A plugin window is named `plugin:<id>:<window>`. Its content is data (a {@link UiNode} tree)
 * the plugin answers to `plugins.view`; a button sends `plugins.action` with the value of
 * every field, and the plugin answers the new content. The plugin also answers a `state`,
 * sent back with the next request, so it can remember things (a page, a filter) without
 * keeping anything itself. When the plugin says its windows are out of date
 * (`plugins.refresh`), the open ones ask again.
 *
 * Windows only {@link PluginViews.load} when they open and {@link PluginViews.release} when they
 * close, so closed windows cost nothing.
 */
import { createStore, produce } from "solid-js/store";
import type { UiNode } from "@protocol/UiNode";
import type { Request } from "@protocol/Request";
import { isClientError, type Client } from "../client/client";
import { isReady, parsePluginPart, pluginCommand, type Plugins } from "./plugins";

/** A window a ready plugin offers. */
export interface PluginWindowInfo {
  /** Its window name in the app: `plugin:<id>:<window>`. */
  key: string;
  /** The plugin's id. */
  plugin: string;
  /** The window's name in its plugin. */
  window: string;
  /** Its title. */
  title: string;
}

/** What one open plugin window shows. */
export interface PluginWindowContent {
  /** The content, once the plugin answered. */
  view: UiNode | null;
  /** The state the plugin answered with it, sent back with the next request. */
  state: unknown;
  /** Why the last request failed, if it did. */
  error: string | null;
  /** `true` while a request runs. */
  busy: boolean;
}

/** The plugin windows service. */
export interface PluginViews {
  /** The windows of the plugins ready to use, in plugin order. */
  windows(): PluginWindowInfo[];
  /** What the window `key` shows (reactive); empty when it was never loaded. */
  content(key: string): PluginWindowContent;
  /** Asks the plugin for the window's content (when it opens, or to refresh it). */
  load(key: string): Promise<void>;
  /** Presses a button: sends its `action` and the fields' `values`, shows the new content. */
  act(key: string, action: string, values: Record<string, string>): Promise<void>;
  /** Forgets a window's content (it closed): it is no longer refreshed. */
  release(key: string): void;
}

/** What a window shows before its first answer. */
const EMPTY: PluginWindowContent = { view: null, state: null, error: null, busy: false };

/** Starts following plugin windows through `client`, using the plugin list of `plugins`. */
export function createPluginViews(client: Client, plugins: Plugins): PluginViews {
  const [open, setOpen] = createStore<Record<string, PluginWindowContent>>({});
  /** Per window, the number of the latest request: older answers are dropped. */
  const latest = new Map<string, number>();

  /** Sends `request` for the window `key` and shows its answer. */
  const send = async (key: string, request: Request) => {
    const n = (latest.get(key) ?? 0) + 1;
    latest.set(key, n);
    setOpen(key, (c) => ({ ...(c ?? EMPTY), busy: true }));
    try {
      const response = await client.request(request);
      if (latest.get(key) !== n || !(key in open)) return;
      if (response.type === "plugin_view") setOpen(key, { view: response.view, state: response.state, error: null, busy: false });
    } catch (e) {
      if (latest.get(key) !== n || !(key in open)) return;
      setOpen(key, (c) => ({ ...(c ?? EMPTY), error: isClientError(e) ? e.message : String(e), busy: false }));
    }
  };

  client.onEvent((event) => {
    if (event.event !== "plugins.refresh") return;
    for (const key of Object.keys(open)) {
      if (parsePluginPart(key)?.plugin === event.data.plugin) void views.load(key);
    }
  });

  const views: PluginViews = {
    windows: () =>
      plugins
        .list()
        .filter(isReady)
        .flatMap((p) => p.windows.map((w) => ({ key: pluginCommand(p.id, w.name), plugin: p.id, window: w.name, title: w.label }))),
    content: (key) => open[key] ?? EMPTY,
    async load(key) {
      const part = parsePluginPart(key);
      if (!part) return;
      if (!(key in open)) setOpen(key, { ...EMPTY });
      await send(key, { method: "plugins.view", params: { plugin: part.plugin, window: part.part, state: open[key]?.state ?? null } });
    },
    async act(key, action, values) {
      const part = parsePluginPart(key);
      if (!part) return;
      await send(key, { method: "plugins.action", params: { plugin: part.plugin, window: part.part, action, values, state: open[key]?.state ?? null } });
    },
    release(key) {
      latest.delete(key);
      setOpen(produce((all) => void delete all[key]));
    },
  };
  return views;
}
